import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as groupsApi from '../api/groups';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/toast';
import Modal from '../components/Modal';
import GroupForm, { emptyGroupForm } from '../components/GroupForm';
import { ErrorAlert, Spinner, EmptyState, formatRelative } from '../components/ui';
import { cn, ui } from '../lib/ui';

export default function Groups() {
  const { user } = useAuth();
  const toast = useToast();

  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadGroups = useCallback(() => {
    groupsApi
      .listGroups({ limit: 50 })
      .then(d => setGroups(d.groups || []))
      .catch(err => {
        setGroups([]);
        setError(err);
      });
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  async function handleCreate(payload) {
    await groupsApi.createGroup(payload);
    toast.success('Group created.');
    setCreating(false);
    loadGroups();
  }

  return (
    <>
      <div className={cn(ui.row, ui.spread, 'mb-4 items-start')}>
        <h1 className="text-title-l">Groups</h1>
        <button type="button" className={ui.btnPrimary} onClick={() => setCreating(true)}>
          New group
        </button>
      </div>
      <ErrorAlert error={error} />
      {groups === null ? (
        <Spinner />
      ) : groups.length === 0 ? (
        <EmptyState>No groups yet. Create one to get started.</EmptyState>
      ) : (
        <div className={cn(ui.grid, ui.gridMd2)}>
          {groups.map(g => (
            <Link
              key={g.groupId}
              to={`/groups/${g.groupId}`}
              className={cn(ui.card, 'block text-inherit no-underline')}
            >
              <h3 className="text-title-s">{g.name}</h3>
              {g.description && <p className={cn(ui.muted, 'text-body-s')}>{g.description}</p>}
              <p className="text-body-s">
                {g.memberCount || 0} member{g.memberCount === 1 ? '' : 's'}
                {g.createdBy === user?.userId && (
                  <span className={cn(ui.pillInfo, 'ml-2')}>Owner</span>
                )}
              </p>
              {g.createdAt && (
                <p className={cn(ui.muted, 'text-body-s')}>Created {formatRelative(g.createdAt)}</p>
              )}
            </Link>
          ))}
        </div>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="New group">
        <GroupForm
          initial={emptyGroupForm()}
          submitLabel="Create group"
          onSubmit={handleCreate}
          onCancel={() => setCreating(false)}
        />
      </Modal>
    </>
  );
}
